import { useMemo, useEffect } from "react";
// @ts-ignore package has no types
import HtmlParser from "react-html-parser";
import { z } from "zod";
import { T, useT } from "@transifex/react";
import { useForm } from "storybook/utils/form";
import { Typography } from "storybook/components/typography";
import { Button } from "storybook/components/button";
import { SectionContent } from "../../styles";
import { PatientResults } from "context/admin-content/types";
import { RiskKey, useRiskProfile } from "context/risk-profile";
import { EligibilityVideo } from "./eligibility-video";
import { Advert } from "components/advert";
import { useTakeAction } from "hooks/use-take-action";

import {
  InfoSection,
  RadioButton,
  RadioGroup,
  Wrapper,
  ButtonContainer,
  ReasonsContainer,
  InstructionsText,
  LeftSection,
} from "./styles";

const noop = () => null;

const schema = z.object({
  eligibility: z.enum(["testing", "breastMri"]),
});

type Eligibility = PatientResults & {
  key: string;
  displayValue: string;
};

type Props = {
  eligibilitiesWithVideosEnabled: Eligibility[];
  setSelectedEligibility: React.Dispatch<
    React.SetStateAction<RiskKey | undefined>
  >;
  riskResult: PatientResults | undefined;
  riskProfileValuesWithMeetsCriteria: any[];
};

type SchemaType = z.infer<typeof schema>;

const RadioComponent = ({
  selected,
  children,
}: {
  selected: boolean;
  children: React.ReactNode;
}) => {
  return (
    <RadioButton
      variant="contained"
      color="default"
      fullWidth={true}
      // @ts-ignore
      component="div"
      // This prevents the button from interfering with the natural browser
      // keyboard navigation of the radio buttons, which is the up and down arrows.
      // We don't tab across the radios, we tab onto them once and press up/down to change selection.
      tabIndex={-1}
      selected={selected}
    >
      {children}
    </RadioButton>
  );
};

export function NextStepsSection({
  eligibilitiesWithVideosEnabled,
  setSelectedEligibility,
  riskResult,
  riskProfileValuesWithMeetsCriteria,
}: Props) {
  const t = useT();
  const values: SchemaType = useMemo(
    () => ({
      eligibility: eligibilitiesWithVideosEnabled[0]
        ?.key as SchemaType["eligibility"],
    }),
    [eligibilitiesWithVideosEnabled]
  );
  const riskProfile = useRiskProfile();

  const riskResultBodyHtml = HtmlParser(riskResult?.bodyHtml || "");

  const { showScheduler } = useTakeAction({ riskResult });

  const { Form, Radio, getValues, setValue } = useForm<SchemaType>({
    schema,
    values,
  });

  const selectedEligibility = getValues("eligibility");

  useEffect(() => {
    setSelectedEligibility(selectedEligibility as RiskKey);
  }, [selectedEligibility, setSelectedEligibility]);

  useEffect(() => {
    const video = document.querySelector("video");
    if (video) {
      video.onended = () => {
        const selectedEligibilityIndex =
          eligibilitiesWithVideosEnabled.findIndex(
            (eligibility) => eligibility.key === selectedEligibility
          );
        const nextEligibility =
          eligibilitiesWithVideosEnabled[selectedEligibilityIndex + 1];

        if (nextEligibility) {
          setValue(
            "eligibility",
            nextEligibility.key as SchemaType["eligibility"],
            {
              shouldValidate: true,
              shouldDirty: true,
              shouldTouch: true,
            }
          );
        }
      };
    }
  }, [eligibilitiesWithVideosEnabled, selectedEligibility, setValue]);

  const showVideos = eligibilitiesWithVideosEnabled.length > 0;

  const riskProfileValuesWithMeetsCriteriaKeys: string[] = [];

  riskProfileValuesWithMeetsCriteria.forEach((value) => {
    riskProfileValuesWithMeetsCriteriaKeys.push(value[0]);
  });

  return (
    <SectionContent className="video">
      <InstructionsText>
        <Typography theme="default" variant="h5" component="h2" color="black">
          <T _str="Instructions" />
        </Typography>
      </InstructionsText>
      {riskResult?.nextStepsHeader && (
        <Typography theme="default" variant="h6" component="h2" color="black">
          {riskResult.nextStepsHeader}
        </Typography>
      )}
      <Wrapper>
        {eligibilitiesWithVideosEnabled.length > 1 && (
          <div>
            <Form onSubmit={noop} id="eligibilities">
              <RadioGroup>
                {eligibilitiesWithVideosEnabled.map((eligibility) => (
                  <Radio
                    key={eligibility.key}
                    component={RadioComponent}
                    theme="default"
                    name="eligibility"
                    value={eligibility.key}
                    displayValue={eligibility.displayValue}
                  />
                ))}
              </RadioGroup>
            </Form>
          </div>
        )}
        <InfoSection>
          <LeftSection>
            {showVideos && (
              <>
                {eligibilitiesWithVideosEnabled.map(
                  (eligibility) =>
                    eligibility.key === selectedEligibility && (
                      <EligibilityVideo
                        key={eligibility.key}
                        videoUrl={eligibility?.videoUrl}
                      />
                    )
                )}
              </>
            )}
            {riskProfile?.riskResultKey === RiskKey.average && (
              <ReasonsContainer>{riskResultBodyHtml}</ReasonsContainer>
            )}
            {showScheduler && (
              <ButtonContainer>
                <Button
                  size="large"
                  theme="default"
                  fullWidth={true}
                  color="secondary"
                  variant="contained"
                  href="/complete/take-action"
                >
                  {riskResult?.callToActionText
                    ? riskResult.callToActionText
                    : t("Request An Appointment")}
                </Button>
              </ButtonContainer>
            )}
          </LeftSection>
          <Advert
            ciqTargetedContent="true"
            ciqInventoryClass="display"
            ciqClass="with-background"
            ciqShowTargetedContent="true"
            ciqCanvas="targeted-content-canvas"
          />
        </InfoSection>
      </Wrapper>
    </SectionContent>
  );
}
